/**
 * Enveloppes et codes d'erreur du contrat d'API v1 (DECISIONS §4.1 et §4.3).
 *
 * Deux formes circulent sur le réseau, et **seulement** deux :
 * - succès : `{ data, meta }` ;
 * - erreur : `{ error: { code, message, details }, meta }`.
 *
 * `ERROR_CODES` est une liste **fermée** : elle recopie exactement DECISIONS §4.3. Tout ajout
 * exige une mise à jour de DECISIONS.md par le chef de projet, puis de ce fichier.
 */
import { z } from 'zod';
/**
 * Liste fermée des codes d'erreur métier (DECISIONS §4.3, v1.7 : 22 codes
 * (`SERVICE_UNAVAILABLE` ajouté en v1.7, A-44)).
 */
export const ERROR_CODES = [
    'VALIDATION_FAILED',
    'AUTH_INVALID_CREDENTIALS',
    'AUTH_TOKEN_EXPIRED',
    'AUTH_REQUIRED',
    'FORBIDDEN',
    'NOT_FOUND',
    'EMAIL_ALREADY_USED',
    'PSEUDO_ALREADY_USED',
    'CONSENT_REQUIRED',
    'RUN_NOT_FOUND',
    'RUN_NOT_ACTIVE',
    'RUN_INCOHERENT',
    'RUN_EXPIRED',
    'RUN_VERSION_MISMATCH',
    'WAVE_ALREADY_CLOSED',
    'CARD_NOT_AVAILABLE',
    'INSUFFICIENT_ECLATS',
    'UPGRADE_MAX_LEVEL',
    'RATE_LIMITED',
    'NOT_IMPLEMENTED',
    'INTERNAL_ERROR',
    'SERVICE_UNAVAILABLE',
];
export const errorCodeSchema = z.enum(ERROR_CODES);
/**
 * Code HTTP associé à chaque code métier — **unique point de vérité** de l'appariement.
 *
 * Recopie exactement le tableau opposable de DECISIONS §4.3 (v1.7). Aucun contrôleur ne doit
 * forcer un statut : `ApiException` et le filtre global lisent cette table.
 */
export const ERROR_CODE_HTTP_STATUS = {
    VALIDATION_FAILED: 400,
    AUTH_INVALID_CREDENTIALS: 401,
    AUTH_TOKEN_EXPIRED: 401,
    AUTH_REQUIRED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    EMAIL_ALREADY_USED: 409,
    PSEUDO_ALREADY_USED: 409,
    CONSENT_REQUIRED: 422,
    RUN_NOT_FOUND: 404,
    RUN_NOT_ACTIVE: 422,
    RUN_INCOHERENT: 422,
    RUN_EXPIRED: 410,
    RUN_VERSION_MISMATCH: 409,
    WAVE_ALREADY_CLOSED: 409,
    CARD_NOT_AVAILABLE: 422,
    INSUFFICIENT_ECLATS: 422,
    UPGRADE_MAX_LEVEL: 422,
    RATE_LIMITED: 429,
    NOT_IMPLEMENTED: 501,
    INTERNAL_ERROR: 500,
    SERVICE_UNAVAILABLE: 503,
};
/**
 * Inverse de `ERROR_CODE_HTTP_STATUS` pour un statut HTTP **sans code métier explicite**
 * (route inexistante, exception Nest nue). Utilisé uniquement par le filtre global.
 */
export function codeDepuisStatutHttp(statut) {
    switch (statut) {
        case 400:
            return 'VALIDATION_FAILED';
        case 401:
            return 'AUTH_REQUIRED';
        case 403:
            return 'FORBIDDEN';
        case 404:
            return 'NOT_FOUND';
        case 409:
            return 'VALIDATION_FAILED';
        case 410:
            return 'RUN_EXPIRED';
        case 422:
            return 'VALIDATION_FAILED';
        case 429:
            return 'RATE_LIMITED';
        case 501:
            return 'NOT_IMPLEMENTED';
        case 503:
            return 'SERVICE_UNAVAILABLE';
        default:
            return 'INTERNAL_ERROR';
    }
}
/** Métadonnées présentes sur **toute** réponse. */
export const apiMetaSchema = z
    .object({
    /** Identifiant de corrélation, à citer dans un signalement. */
    requestId: z.string().uuid(),
})
    .strict();
/** Construit une réponse de succès. */
export function apiSuccess(data, meta) {
    return { data, meta };
}
/** Construit une réponse d'erreur. */
export function apiError(code, message, meta, details) {
    return details === undefined
        ? { error: { code, message }, meta }
        : { error: { code, message, details }, meta };
}
/** Schéma zod de l'enveloppe d'erreur (validation côté client et tests serveur). */
export const apiErrorSchema = z
    .object({
    error: z
        .object({
        code: errorCodeSchema,
        message: z.string().min(1),
        details: z.record(z.string(), z.unknown()).optional(),
    })
        .strict(),
    meta: apiMetaSchema,
})
    .strict();
/** Fabrique le schéma zod d'une enveloppe de succès pour un type de données donné. */
export function apiSuccessSchema(data) {
    return z.object({ data, meta: apiMetaSchema }).strict();
}
/**
 * Décode une charge utile inconnue en enveloppe typée.
 *
 * Utilisé par le client web et par les tests : une réponse hors contrat devient une
 * `INTERNAL_ERROR` explicite plutôt qu'un `undefined` silencieux.
 */
export function parseEnvelope(payload, dataSchema) {
    const meta = apiMetaSchema.safeParse(payload !== null && typeof payload === 'object'
        ? payload['meta']
        : undefined);
    const metaValue = meta.success
        ? meta.data
        : { requestId: '00000000-0000-0000-0000-000000000000' };
    const erreur = apiErrorSchema.safeParse(payload);
    if (erreur.success) {
        const details = erreur.data.error.details;
        return details === undefined
            ? {
                ok: false,
                code: erreur.data.error.code,
                message: erreur.data.error.message,
                meta: erreur.data.meta,
            }
            : {
                ok: false,
                code: erreur.data.error.code,
                message: erreur.data.error.message,
                details,
                meta: erreur.data.meta,
            };
    }
    const succes = apiSuccessSchema(dataSchema).safeParse(payload);
    if (succes.success) {
        const decode = succes.data;
        return { ok: true, data: decode.data, meta: decode.meta };
    }
    return {
        ok: false,
        code: 'INTERNAL_ERROR',
        message: 'Réponse hors contrat d’API',
        meta: metaValue,
    };
}
/** Garde de type sur l'enveloppe de succès. */
export function isApiSuccess(payload) {
    return 'data' in payload;
}
/** Garde de type sur l'enveloppe d'erreur. */
export function isApiError(payload) {
    return 'error' in payload;
}
//# sourceMappingURL=envelope.js.map